import React, { useState, useEffect, useCallback, useRef } from "react";
import "./ApplicantTab.css";

// Empty in dev -> relative "/api" paths, proxied to the local backend (see frontend/.env.*).
const RAILWAY = process.env.REACT_APP_API_BASE || "";

const CLIENT_TYPES = [
  { label: "All Clients", value: "all" },
  { label: "GEM Only",    value: "gem" },
  { label: "Facility Only", value: "facility" },
  { label: "Home Care Only", value: "homecare" },
];

function typeLabel(clientType) {
  const t = (clientType || "").toLowerCase();
  if (t.startsWith("4b13e")) return "GEM";
  if (t.startsWith("884e8f")) return "Facility";
  if (t.startsWith("505c")) return "Home Care";
  return "Other";
}

export default function ApplicantTab({ clients }) {
  const [applicants, setApplicants]         = useState([]);
  const [loadingApplicants, setLoading]     = useState(false);
  const [search, setSearch]                 = useState("");
  const [selectedApplicant, setSelected]    = useState(null);
  const [editedAddress, setEditedAddress]   = useState("");
  const [editingAddress, setEditingAddress] = useState(false);
  const [typeFilter, setTypeFilter]         = useState("all");
  const [clientDistances, setClientDistances] = useState({});
  const [loadingDist, setLoadingDist]       = useState(false);
  const [dropdownOpen, setDropdownOpen]     = useState(false);
  const searchRef = useRef(null);

  // Load applicants once
  useEffect(() => {
    setLoading(true);
    fetch(`${RAILWAY}/api/applicants`)
      .then(r => r.json())
      .then(d => setApplicants(d.applicants || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Filter search dropdown
  const filtered = search.length < 2 ? [] : applicants.filter(a =>
    `${a.firstName} ${a.lastName}`.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelectApplicant = useCallback((applicant) => {
    setSelected(applicant);
    setEditedAddress(applicant.address || "");
    setEditingAddress(false);
    setClientDistances({});
    setSearch(`${applicant.firstName} ${applicant.lastName}`);
    setDropdownOpen(false);
  }, []);

  // Compute distances from applicant to all clients
  const computeDistances = useCallback(async (address) => {
    if (!address || !clients.length) return;
    setLoadingDist(true);
    try {
      const workerAddresses = clients
        .filter(c => c.address)
        .map(c => ({ id: c.id, address: c.address }));

      const res = await fetch(`${RAILWAY}/api/distances`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientAddress: address,
          workerAddresses,
          includeTransit: true,
        }),
      });
      const data = await res.json();
      const map = {};
      (data.distances || []).forEach(d => {
        // Backend returns driving distance/time only; estimate public-transit
        // time as ~2.5x drive time (same approximation as the dispatch view).
        const drive = d.durationMinutes;
        map[d.workerId] = {
          miles:   d.distanceMiles,
          minutes: drive,
          transit: (drive != null) ? Math.round((drive * 2.5) / 5) * 5 : null,
        };
      });
      setClientDistances(map);
    } catch (e) { console.error("Distance error:", e); }
    finally { setLoadingDist(false); }
  }, [clients]);

  useEffect(() => {
    if (selectedApplicant && editedAddress) computeDistances(editedAddress);
  }, [editedAddress, selectedApplicant, computeDistances]);

  // Filter clients by type
  const filteredClients = clients.filter(c => {
    if (typeFilter === "all") return true;
    const label = typeLabel(c.clientType).toLowerCase().replace(" ", "");
    return label === typeFilter;
  });

  // Rank clients by driving distance
  const rankedClients = filteredClients
    .map(c => ({ ...c, ...clientDistances[c.id] }))
    .filter(c => c.miles !== undefined)
    .sort((a, b) => (parseFloat(a.miles)||9999) - (parseFloat(b.miles)||9999));

  // Metrics
  const within30Drive   = Object.values(clientDistances).filter(d => d.minutes !== null && d.minutes <= 30).length;
  const within60Transit = Object.values(clientDistances).filter(d => d.transit !== null && d.transit <= 60).length;
  const hasDistances    = Object.keys(clientDistances).length > 0;

  return (
    <div className="applicant-tab">
      {/* ── Left search panel ── */}
      <div className="applicant-search-panel">
        <div className="search-header">
          <h3 className="search-title">Applicant Proximity</h3>
          <p className="search-sub">Search an applicant to see which clients are nearest to them</p>
        </div>

        {/* Name search */}
        <div className="search-field">
          <label className="field-label">Applicant Name</label>
          <div className="search-input-wrap" ref={searchRef}>
            <input
              className="search-input"
              type="text"
              placeholder={loadingApplicants ? "Loading applicants…" : "Type name to search…"}
              value={search}
              onChange={e => { setSearch(e.target.value); setDropdownOpen(true); setSelected(null); }}
              onFocus={() => setDropdownOpen(true)}
              disabled={loadingApplicants}
            />
            {search && <button className="clear-btn" onClick={() => { setSearch(""); setSelected(null); setClientDistances({}); }}>✕</button>}
            {dropdownOpen && filtered.length > 0 && (
              <div className="search-dropdown">
                {filtered.slice(0, 10).map(a => (
                  <div key={a.id} className="dropdown-item" onClick={() => handleSelectApplicant(a)}>
                    <span className="dropdown-name">{a.firstName} {a.lastName}</span>
                    <span className="dropdown-meta">{a.role || "No role"} · {a.address ? a.address.split(",")[0] : "No address"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Selected applicant card */}
        {selectedApplicant && (
          <div className="applicant-card">
            <div className="applicant-initials">
              {(selectedApplicant.firstName||"?")[0]}{(selectedApplicant.lastName||"?")[0]}
            </div>
            <div className="applicant-details">
              <div className="applicant-name">{selectedApplicant.firstName} {selectedApplicant.lastName}</div>
              {selectedApplicant.phone && <div className="applicant-meta">{selectedApplicant.phone}</div>}
              {selectedApplicant.role  && <div className="applicant-meta">{selectedApplicant.role}</div>}
              <div className="address-row">
                {editingAddress ? (
                  <div className="address-edit">
                    <input
                      className="address-input"
                      value={editedAddress}
                      onChange={e => setEditedAddress(e.target.value)}
                      placeholder="Enter address…"
                    />
                    <button className="addr-save-btn" onClick={() => setEditingAddress(false)}>✓ Use this address</button>
                  </div>
                ) : (
                  <div className="address-display">
                    <span className="address-text">{editedAddress || "No address on file"}</span>
                    <button className="edit-addr-btn" onClick={() => setEditingAddress(true)}>✎ Edit</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Client type filter */}
        <div className="search-field">
          <label className="field-label">Client Type Filter</label>
          <div className="type-filter-buttons">
            <button
              className={`type-btn type-btn-all ${typeFilter === "all" ? "active" : ""}`}
              onClick={() => setTypeFilter("all")}
            >All Clients</button>
            <div className="type-filter-row">
              {CLIENT_TYPES.filter(ct => ct.value !== "all").map(ct => (
                <button
                  key={ct.value}
                  className={`type-btn ${typeFilter === ct.value ? "active" : ""}`}
                  onClick={() => setTypeFilter(ct.value)}
                >{ct.label}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Status */}
        {selectedApplicant && (
          <div className="search-status">
            {loadingDist
              ? <span className="status-loading"><span className="spinner-sm"/> Computing distances…</span>
              : hasDistances
                ? <span className="status-count">{rankedClients.length} clients ranked by proximity</span>
                : <span className="status-waiting">Select an applicant to rank nearby clients</span>
            }
          </div>
        )}

        {/* ── Metrics panel ── */}
        {hasDistances && !loadingDist && (
          <div className="metrics-panel">
            <div className="metrics-title">Coverage Summary</div>
            <div className="metric-card">
              <div className="metric-icon">🚗</div>
              <div className="metric-body">
                <div className="metric-number">{within30Drive}</div>
                <div className="metric-label">clients within<br/>30 min drive</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">🚌</div>
              <div className="metric-body">
                <div className="metric-number">{within60Transit}</div>
                <div className="metric-label">clients within<br/>60 min transit</div>
              </div>
            </div>
            <div className="metric-note">
              Transit times are estimated (driving × 2.5). Encourage applicants to verify with SEPTA for exact routes.
            </div>
          </div>
        )}
      </div>

      {/* ── Right results panel ── */}
      <div className="applicant-results">
        {!selectedApplicant && (
          <div className="results-empty">
            <div className="empty-icon">◈</div>
            <p>Search for an applicant above<br/>to see nearby clients ranked by proximity</p>
          </div>
        )}

        {selectedApplicant && !hasDistances && !loadingDist && (
          <div className="results-empty">
            <p>Enter the applicant's address to rank nearby clients</p>
          </div>
        )}

        {selectedApplicant && loadingDist && (
          <div className="results-empty">
            <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }}/>
            <p style={{ marginTop: 12 }}>Computing distances to all clients…</p>
          </div>
        )}

        {rankedClients.map((client, i) => {
          const label = typeLabel(client.clientType);
          return (
            <div key={client.id} className="client-result-card fade-up" style={{ animationDelay: `${i*25}ms` }}>
              <div className="client-rank">#{i+1}</div>
              <div className="client-result-info">
                <div className="client-result-name">{client.fullName}</div>
                <div className="client-result-meta">
                  <span className={`client-type-chip type-${label.toLowerCase().replace(" ","")}`}>{label}</span>
                  {client.address && <span className="client-address">{client.address.split(",").slice(0,2).join(",")}</span>}
                </div>
              </div>
              <div className="client-result-dist">
                <div className="dist-row">
                  <span className="dist-icon">🚗</span>
                  <span className="dist-val">{client.miles} mi</span>
                  <span className="dist-time">~{client.minutes} min</span>
                </div>
                <div className="dist-row transit">
                  <span className="dist-icon">🚌</span>
                  <span className="dist-val">~{client.transit} min</span>
                  <span className="dist-label">est. transit</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
